import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import cn from 'classnames';
import { useDispatch } from 'react-redux';

import { SEARCH_PRICE_FILTER_MAX, SEARCH_PRICE_FILTER_MIN } from 'constants/appConstants';
import DoubleSlider from 'components/common/DoubleSlider';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { updatePriceRange } from 'actions/search';
import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/search/priceSlider.scss';

export const MIN_LABEL = 'Minimum price range';
export const MAX_LABEL = 'Maximum price range';

export const PriceSlider = ({ className, isToggled, minDefault, maxDefault, updatePriceRange }) => {
  const dispatch = useDispatch();
  const { testId } = useMartyContext();

  const sliderRef = useRef();

  // Default in case of null/undefined
  const minDefaultValue = typeof minDefault === 'number' ? minDefault : SEARCH_PRICE_FILTER_MIN;
  const maxDefaultValue = typeof maxDefault === 'number' ? maxDefault : SEARCH_PRICE_FILTER_MAX;

  const [range, setRange] = useState({ min: minDefaultValue, max: maxDefaultValue });
  const { min, max } = range;

  useEffect(() => {
    // Manually reset slider if any of the reset buttons are clicked and we're back to the default state.
    if (minDefault === SEARCH_PRICE_FILTER_MIN && [SEARCH_PRICE_FILTER_MAX, null].includes(maxDefault)) {
      sliderRef.current?.reset();
      setRange({ min: minDefaultValue, max: maxDefaultValue });
    }
  }, [minDefault, maxDefault, minDefaultValue, maxDefaultValue]);

  const deboucedUpdatePriceRange = useCallback(debounce(priceRange => dispatch(updatePriceRange(priceRange)), 800), []);

  const onChange = useCallback(priceRange => {
    setRange(priceRange);
    // Debouncing this to prevent excessive API calls to search
    deboucedUpdatePriceRange(priceRange);
  }, [deboucedUpdatePriceRange]);

  return (
    <div className={cn(className, css.container, { [css.hidden]: isToggled })}>
      <div className={css.copyContainer}>
        <p data-test-id={testId('priceRangeMin')}>Min ${min}</p>
        <p data-test-id={testId('priceRangeMax')}>Max ${max} {max === SEARCH_PRICE_FILTER_MAX && '+'}</p>
      </div>
      <DoubleSlider
        ref={sliderRef}
        minLabel={MIN_LABEL}
        maxLabel={MAX_LABEL}
        minLimit={SEARCH_PRICE_FILTER_MIN}
        maxLimit={SEARCH_PRICE_FILTER_MAX}
        minDefault={minDefaultValue}
        maxDefault={maxDefaultValue}
        onChange={onChange}
      />
    </div>
  );
};

PriceSlider.defaultProps = {
  updatePriceRange
};

export default withErrorBoundary('PriceSlider', PriceSlider);
